import type { CurriculumModuleGroupSummary } from '@/types/quizSet'

interface Props {
  data?: CurriculumModuleGroupSummary
  isLoading: boolean
  error: unknown
  onRetry: () => void
}

export function CurriculumModuleGroupSummaryPanel({
  data,
  isLoading,
  error,
  onRetry,
}: Props) {
  if (isLoading) {
    return <div role="status" className="rounded-lg border border-slate-200 p-4 text-sm text-slate-500">조회 중...</div>
  }
  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        모듈 현황을 불러오지 못했습니다.
        <button type="button" onClick={onRetry} className="ml-3 font-medium underline">다시 시도</button>
      </div>
    )
  }
  if (!data) {
    return <div className="rounded-lg border border-slate-200 p-4 text-sm text-slate-400">검수할 커리큘럼을 선택하세요</div>
  }

  return (
    <section className="space-y-3" aria-label="커리큘럼 모듈 현황">
      <div>
        <h3 className="font-semibold text-slate-900">{data.curriculumTitle}</h3>
        <p className="text-xs text-slate-500">curriculum #{data.curriculumId}</p>
      </div>
      {data.courses.length === 0 ? (
        <p className="text-sm text-slate-500">활성 코스가 없습니다</p>
      ) : data.courses.map(course => (
        <details key={course.courseId} open className="rounded-lg border border-slate-200 bg-white">
          <summary className="cursor-pointer px-4 py-3 font-medium text-slate-800">{course.courseTitle}</summary>
          {course.modules.length === 0 ? (
            <p className="px-4 pb-4 text-sm text-slate-500">활성 모듈이 없습니다</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-t bg-slate-50 text-left text-xs font-semibold text-slate-500">
                    <th rowSpan={2} className="px-4 py-2 align-middle">Module</th>
                    <th rowSpan={2} className="px-3 py-2 text-right align-middle">전체 레슨</th>
                    <th colSpan={3} className="border-l border-slate-200 px-4 py-2 text-center">연결 레슨</th>
                    <th colSpan={3} className="border-l border-slate-200 px-4 py-2 text-center">그룹</th>
                  </tr>
                  <tr className="border-t border-slate-200 bg-slate-50 text-center text-[11px] font-semibold text-slate-500">
                    <th className="border-l border-slate-200 px-3 py-2">전체</th>
                    <th className="px-3 py-2 text-blue-700">AIQ</th>
                    <th className="px-3 py-2 text-amber-700">따대깨</th>
                    <th className="border-l border-slate-200 px-3 py-2">전체</th>
                    <th className="px-3 py-2 text-blue-700">AIQ</th>
                    <th className="px-4 py-2 text-amber-700">따대깨</th>
                  </tr>
                </thead>
                <tbody>
                  {course.modules.map(module => (
                    <tr key={`${course.courseId}-${module.moduleId}`} className="border-t">
                      <td className="px-4 py-2 text-slate-800">
                        {module.moduleTitle}
                        {module.mappedLessonCount === 0 ? <span className="ml-2 text-xs text-rose-600">미연결</span> : null}
                      </td>
                      <td className="px-3 py-2 text-right">{module.lessonCount}</td>
                      <SummaryCell value={module.mappedLessonCount} separated warning={module.mappedLessonCount < module.lessonCount} />
                      <SummaryCell value={module.qbAiqMappedLessonCount} tone="aiq" />
                      <SummaryCell value={module.legacyAiqMappedLessonCount} tone="legacy" />
                      <SummaryCell value={module.groupCount} separated />
                      <SummaryCell value={module.qbAiqGroupCount} tone="aiq" />
                      <SummaryCell value={module.legacyAiqGroupCount} tone="legacy" last />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>
      ))}
    </section>
  )
}

function SummaryCell({
  value,
  tone,
  separated = false,
  warning = false,
  last = false,
}: {
  value: number
  tone?: 'aiq' | 'legacy'
  separated?: boolean
  warning?: boolean
  last?: boolean
}) {
  const toneClass = tone === 'aiq'
    ? 'text-blue-700'
    : tone === 'legacy'
      ? 'text-amber-700'
      : warning
        ? 'font-semibold text-amber-700'
        : 'text-slate-700'
  return (
    <td className={`${separated ? 'border-l border-slate-100' : ''} ${last ? 'pr-4' : ''} px-3 py-2 text-right tabular-nums ${toneClass}`}>
      {value}
    </td>
  )
}
